/**
 * Pure vanity cabinet resize logic.
 * No Firestore, no async. Handles cabinet/countertop dimension changes
 * and sink/faucet repositioning.
 *
 * Shared between cloud functions and web app.
 */

const {
  INCHES_TO_METERS,
  VANITY_DIMENSIONS,
} = require('../vanity-assembly/constants');
const { getWallInwardNormal } = require('../vanity-assembly/placementUtils');
const { alongWallAxis } = require('./wallAlignedAxis');

// Inline from vanityRules.js — simple width threshold function
const DOUBLE_VANITY_COMPACT_MAX = 72; // inches
const DOUBLE_VANITY_MEDIUM_MAX = 96; // inches
function selectDoubleVanityCase(vanityWidthMeters) {
  const widthInches = vanityWidthMeters / INCHES_TO_METERS;
  if (widthInches < DOUBLE_VANITY_COMPACT_MAX) return 'compact';
  if (widthInches < DOUBLE_VANITY_MEDIUM_MAX) return 'medium';
  return 'wide';
}

/**
 * Detect whether a faucet is wall-mounted.
 * Checks explicit `wallMounted` flag first, then falls back to parent analysis:
 * wall-mounted faucets have no countertop parent.
 */
function isWallMountedFaucet(faucet) {
  if (faucet.wallMounted === true) return true;
  if (
    Array.isArray(faucet.parents) &&
    faucet.parents.length > 0 &&
    !faucet.parents.some((p) => p.type === 'countertop')
  ) {
    return true;
  }
  return false;
}

/**
 * Adjust vanity cabinet dimensions and reposition sinks/faucets.
 *
 * @param {Object} options
 * @param {Object} options.cabinet - cabinet object
 * @param {Object} options.countertop - countertop object (may be null)
 * @param {Array}  options.sinks - sink objects
 * @param {Array}  options.faucets - faucet objects
 * @param {number} options.expandLeft - left expansion amount
 * @param {number} options.expandRight - right expansion amount
 * @param {number} options.reduceLeft - left reduction amount
 * @param {number} options.reduceRight - right reduction amount
 * @param {boolean} options.depthChangeNeeded
 * @param {number} options.newVanityDepth
 * @param {number} options.vanityWidth - current width
 * @param {number} options.vanityDepth - current depth
 * @param {Object} options.vanityCenter - current center { x, y, z }
 * @param {Object} options.wallDir - { x, z } unit vector along wall
 * @param {Object} options.wallNormal - { x, z } inward normal
 * @param {Object} options.spaceInfo - from calculateAvailableWallSpace
 * @param {boolean} options.isDoubleSink
 * @param {boolean} options.isCabinetRotated90
 * @param {Function} options.projectOntoWall - projection helper
 * @returns {{ updates: Map, centerShift, depthCenterShift, newVanityCenter, newVanityWidth, expandedLeft, expandedRight }}
 */
function adjustVanityCabinet({
  cabinet,
  countertop,
  sinks,
  faucets,
  expandLeft,
  expandRight,
  reduceLeft,
  reduceRight,
  depthChangeNeeded,
  newVanityDepth,
  vanityWidth,
  vanityDepth,
  vanityCenter,
  wallDir,
  wallNormal,
  spaceInfo,
  isDoubleSink,
  isCabinetRotated90,
  projectOntoWall,
}) {
  const updates = new Map();

  // --- Direct edge positioning ---
  const oldHalfWidth = vanityWidth / 2;
  const oldLeftEdge = {
    x: vanityCenter.x - oldHalfWidth * wallDir.x,
    z: vanityCenter.z - oldHalfWidth * wallDir.z,
  };
  const oldRightEdge = {
    x: vanityCenter.x + oldHalfWidth * wallDir.x,
    z: vanityCenter.z + oldHalfWidth * wallDir.z,
  };

  const targetLeftEdge = {
    x: oldLeftEdge.x - expandLeft * wallDir.x + reduceLeft * wallDir.x,
    z: oldLeftEdge.z - expandLeft * wallDir.z + reduceLeft * wallDir.z,
  };
  const targetRightEdge = {
    x: oldRightEdge.x + expandRight * wallDir.x - reduceRight * wallDir.x,
    z: oldRightEdge.z + expandRight * wallDir.z - reduceRight * wallDir.z,
  };

  const newVanityCenterXZ = {
    x: (targetLeftEdge.x + targetRightEdge.x) / 2,
    z: (targetLeftEdge.z + targetRightEdge.z) / 2,
  };

  const edgeDx = targetRightEdge.x - targetLeftEdge.x;
  const edgeDz = targetRightEdge.z - targetLeftEdge.z;
  const newVanityWidth = Math.sqrt(edgeDx * edgeDx + edgeDz * edgeDz);

  const centerShift =
    (newVanityCenterXZ.x - vanityCenter.x) * wallDir.x +
    (newVanityCenterXZ.z - vanityCenter.z) * wallDir.z;

  const depthChange = newVanityDepth - vanityDepth;
  const depthCenterShift = depthChange / 2;

  const newVanityCenter = {
    x: newVanityCenterXZ.x + wallNormal.x * depthCenterShift,
    y: vanityCenter.y,
    z: newVanityCenterXZ.z + wallNormal.z * depthCenterShift,
  };

  // --- Update cabinet ---
  if (cabinet) {
    const updatedCabinet = { ...cabinet };
    const cabinetDims = updatedCabinet.dimensions;
    if (isCabinetRotated90) {
      updatedCabinet.dimensions = {
        ...cabinetDims,
        z: newVanityWidth,
        x: depthChangeNeeded ? newVanityDepth : cabinetDims.x,
      };
    } else {
      updatedCabinet.dimensions = {
        ...cabinetDims,
        x: newVanityWidth,
        z: depthChangeNeeded ? newVanityDepth : cabinetDims.z,
      };
    }
    updatedCabinet.center = { ...newVanityCenter, y: updatedCabinet.center.y };
    updates.set(cabinet.id, updatedCabinet);
  }

  // --- Update countertop ---
  if (countertop) {
    const updatedCountertop = { ...countertop };
    const overhang = VANITY_DIMENSIONS.countertop.overhang;

    // Suppress overhang on sides where a wall or fixture is close enough
    // that the overhang would extend into it
    const leftObType = spaceInfo.leftObstruction?.type;
    const rightObType = spaceInfo.rightObstruction?.type;
    const hasObstructionOnLeft = leftObType && spaceInfo.leftSpace < overhang;
    const hasObstructionOnRight =
      rightObType && spaceInfo.rightSpace < overhang;
    const leftOverhang = hasObstructionOnLeft ? 0 : overhang;
    const rightOverhang = hasObstructionOnRight ? 0 : overhang;

    const countertopWidthWithOverhang =
      newVanityWidth + leftOverhang + rightOverhang;
    const countertopDepthWithOverhang = newVanityDepth + overhang;

    const overhangCenterOffset = (rightOverhang - leftOverhang) / 2;

    // Which of the countertop's own axes runs along the wall. This used to
    // be `dimensions.z > dimensions.x`, a size comparison rather than an
    // orientation test — see wallAlignedAxis.js and issue #1412.
    const currentDims = updatedCountertop.dimensions;
    const widthAxis = alongWallAxis(updatedCountertop.rotation?.y, wallDir);
    const depthAxis = widthAxis === 'x' ? 'z' : 'x';

    updatedCountertop.dimensions = {
      ...currentDims,
      [widthAxis]: countertopWidthWithOverhang,
      [depthAxis]: depthChangeNeeded
        ? countertopDepthWithOverhang
        : currentDims[depthAxis],
    };

    // Shift center for left/right overhang asymmetry along wall,
    // and forward by half the front overhang along wall normal
    // so the back edge stays flush with the wall
    const frontShift = overhang / 2;
    updatedCountertop.center = {
      x:
        newVanityCenter.x +
        overhangCenterOffset * wallDir.x +
        wallNormal.x * frontShift,
      y: updatedCountertop.center.y,
      z:
        newVanityCenter.z +
        overhangCenterOffset * wallDir.z +
        wallNormal.z * frontShift,
    };
    updates.set(countertop.id, updatedCountertop);
  }

  // --- Reposition sinks ---
  if (isDoubleSink && sinks.length >= 2) {
    const currentSinkSpread =
      Math.abs(
        projectOntoWall(sinks[0].center) - projectOntoWall(sinks[1].center)
      ) || vanityWidth * 0.5;
    const spreadRatio = currentSinkSpread / vanityWidth;
    const newSinkSpread = newVanityWidth * spreadRatio;
    const spreadDelta = (newSinkSpread - currentSinkSpread) / 2;

    const sortedSinks = [...sinks].sort(
      (a, b) => projectOntoWall(a.center) - projectOntoWall(b.center)
    );
    const leftSink = sortedSinks[0];

    for (const sink of sinks) {
      const isLeftSink = sink.id === leftSink.id;
      const sinkShift = centerShift + (isLeftSink ? -spreadDelta : spreadDelta);

      const updatedSink = { ...sink };
      updatedSink.center = {
        x:
          sink.center.x +
          wallDir.x * sinkShift +
          wallNormal.x * depthCenterShift,
        y: sink.center.y,
        z:
          sink.center.z +
          wallDir.z * sinkShift +
          wallNormal.z * depthCenterShift,
      };
      updates.set(sink.id, updatedSink);

      // Move associated faucet with the sink
      const associatedFaucet = faucets.find((f) => {
        const faucetProj = projectOntoWall(f.center);
        const sinkProj = projectOntoWall(sink.center);
        return Math.abs(faucetProj - sinkProj) < 0.15;
      });
      if (associatedFaucet) {
        const updatedFaucet = { ...associatedFaucet };
        // Wall-mounted faucets stay at the wall — don't shift in the depth direction
        const wallMounted = isWallMountedFaucet(associatedFaucet);
        console.log('[adjustVanityCabinet] Faucet depth check (double):', {
          faucetId: associatedFaucet.id,
          wallMountedFlag: associatedFaucet.wallMounted,
          wallMountedDetected: wallMounted,
          depthCenterShift,
        });
        const faucetDepthShift = wallMounted ? 0 : depthCenterShift;
        updatedFaucet.center = {
          x:
            associatedFaucet.center.x +
            wallDir.x * sinkShift +
            wallNormal.x * faucetDepthShift,
          y: associatedFaucet.center.y,
          z:
            associatedFaucet.center.z +
            wallDir.z * sinkShift +
            wallNormal.z * faucetDepthShift,
        };
        updates.set(associatedFaucet.id, updatedFaucet);
      }
    }
  } else {
    // Single sink: just shift with center
    for (const sink of sinks) {
      const updatedSink = { ...sink };
      updatedSink.center = {
        x:
          sink.center.x +
          wallDir.x * centerShift +
          wallNormal.x * depthCenterShift,
        y: sink.center.y,
        z:
          sink.center.z +
          wallDir.z * centerShift +
          wallNormal.z * depthCenterShift,
      };
      updates.set(sink.id, updatedSink);
    }

    for (const faucet of faucets) {
      const updatedFaucet = { ...faucet };
      // Wall-mounted faucets stay at the wall — don't shift in the depth direction
      const wallMounted = isWallMountedFaucet(faucet);
      console.log('[adjustVanityCabinet] Faucet depth check (single):', {
        faucetId: faucet.id,
        wallMountedFlag: faucet.wallMounted,
        wallMountedDetected: wallMounted,
        parents: faucet.parents,
        depthCenterShift,
      });
      const faucetDepthShift = wallMounted ? 0 : depthCenterShift;
      updatedFaucet.center = {
        x:
          faucet.center.x +
          wallDir.x * centerShift +
          wallNormal.x * faucetDepthShift,
        y: faucet.center.y,
        z:
          faucet.center.z +
          wallDir.z * centerShift +
          wallNormal.z * faucetDepthShift,
      };
      updates.set(faucet.id, updatedFaucet);
    }
  }

  return {
    updates,
    centerShift,
    depthCenterShift,
    newVanityCenter,
    newVanityWidth,
    expandedLeft: expandLeft > 0,
    expandedRight: expandRight > 0,
  };
}

/**
 * Calculate double vanity layout configuration based on width.
 *
 * @param {Object} options
 * @param {number} options.vanityWidth - current vanity width
 * @param {Object} options.vanityCenter - { x, y, z }
 * @param {Function} options.projectOntoWall - projection helper
 * @returns {Object} layout config with case, sink targets, mirror constraints
 */
function calculateDoubleVanityLayout({
  vanityWidth,
  vanityCenter,
  projectOntoWall,
}) {
  const doubleVanityCase = selectDoubleVanityCase(vanityWidth);
  const vanityCenterProj = projectOntoWall(vanityCenter);
  const vanityLeftProj = vanityCenterProj - vanityWidth / 2;
  const vanityRightProj = vanityCenterProj + vanityWidth / 2;

  let targetLeftSinkProj, targetRightSinkProj;
  let maxMirrorWidthInches;
  let targetSconceCount;

  if (doubleVanityCase === 'compact') {
    // 1:0.5:1 ratio (left edge : between-sink gap : right edge), with a
    // 1" floor on the gap to keep sink cutouts apart on tiny cabinets.
    const sinkW = VANITY_DIMENSIONS.sink.width;
    const minGap = 1 * INCHES_TO_METERS;
    const slack = vanityWidth - 2 * sinkW;
    const gap = Math.max(minGap, slack / 5);
    const edge = (slack - gap) / 2;
    const sinkInset = edge + sinkW / 2;
    targetLeftSinkProj = vanityLeftProj + sinkInset;
    targetRightSinkProj = vanityRightProj - sinkInset;
    maxMirrorWidthInches = 24;
    targetSconceCount = 2;
  } else if (doubleVanityCase === 'medium') {
    // 1:2:1 ratio (left edge : between-sink gap : right edge).
    // edge = (W − 2·sinkW)/4 and gap = 2·edge → sinkInset = W/4.
    const sinkInset = vanityWidth / 4;
    targetLeftSinkProj = vanityLeftProj + sinkInset;
    targetRightSinkProj = vanityRightProj - sinkInset;
    maxMirrorWidthInches = 27;
    targetSconceCount = 3;
  } else {
    const sinkInset = 27 * INCHES_TO_METERS;
    targetLeftSinkProj = vanityLeftProj + sinkInset;
    targetRightSinkProj = vanityRightProj - sinkInset;
    maxMirrorWidthInches = 30;
    targetSconceCount = 4;
  }

  return {
    case: doubleVanityCase,
    targetSconceCount,
    targetLeftSinkProj,
    targetRightSinkProj,
    maxMirrorWidthInches,
  };
}

/**
 * Reposition double-sink pairs to case-based target positions.
 *
 * @param {Object} options
 * @param {Array}  options.sinks - sink objects
 * @param {Array}  options.faucets - faucet objects
 * @param {number} options.targetLeftSinkProj
 * @param {number} options.targetRightSinkProj
 * @param {Object} options.wallStart - { x, z }
 * @param {Object} options.wallDir - { x, z }
 * @param {Object} options.wallNormal - { x, z }
 * @param {Function} options.projectOntoWall
 * @returns {Map} id → updated object
 */
function repositionDoubleSinks({
  sinks,
  faucets,
  targetLeftSinkProj,
  targetRightSinkProj,
  wallStart,
  wallDir,
  wallNormal,
  projectOntoWall,
}) {
  const updates = new Map();

  const sinksWithProj = sinks.map((s) => ({
    sink: s,
    proj: projectOntoWall(s.center),
  }));
  sinksWithProj.sort((a, b) => a.proj - b.proj);
  const leftSink = sinksWithProj[0].sink;
  const rightSink = sinksWithProj[sinksWithProj.length - 1].sink;

  // Helper: reposition a sink and its associated faucet
  const repositionSink = (sink, targetProj) => {
    const existingSink = updates.get(sink.id) || sink;
    const sinkDepthFromWall =
      (existingSink.center.x - wallStart.x) * wallNormal.x +
      (existingSink.center.z - wallStart.z) * wallNormal.z;

    const newX =
      wallStart.x + wallDir.x * targetProj + wallNormal.x * sinkDepthFromWall;
    const newZ =
      wallStart.z + wallDir.z * targetProj + wallNormal.z * sinkDepthFromWall;

    updates.set(sink.id, {
      ...existingSink,
      center: { ...existingSink.center, x: newX, z: newZ },
    });

    // Move associated faucet
    const faucet = faucets.find((f) => {
      const faucetProj = projectOntoWall(f.center);
      const sinkProj = projectOntoWall(sink.center);
      return Math.abs(faucetProj - sinkProj) < 0.2;
    });
    if (faucet) {
      const existingFaucet = updates.get(faucet.id) || faucet;
      const faucetDepthFromWall =
        (existingFaucet.center.x - wallStart.x) * wallNormal.x +
        (existingFaucet.center.z - wallStart.z) * wallNormal.z;

      updates.set(faucet.id, {
        ...existingFaucet,
        center: {
          ...existingFaucet.center,
          x:
            wallStart.x +
            wallDir.x * targetProj +
            wallNormal.x * faucetDepthFromWall,
          z:
            wallStart.z +
            wallDir.z * targetProj +
            wallNormal.z * faucetDepthFromWall,
        },
      });
    }
  };

  repositionSink(leftSink, targetLeftSinkProj);
  repositionSink(rightSink, targetRightSinkProj);

  return updates;
}

module.exports = {
  adjustVanityCabinet,
  calculateDoubleVanityLayout,
  repositionDoubleSinks,
};
